"use client";

import type { RefObject } from "react";

import { useLayoutEffect, useRef } from "react";

type TakeoverSectionMinHeightOptions = {
  /** The section holds its tallest height while this is true. */
  isHolding: boolean;
  /** The section's height is recorded while this is true. */
  isMeasuring: boolean;
};

/**
 * Keeps a section the lead flow takes over from shrinking between steps.
 * While the flow is in the section, the tallest height the section reaches
 * is recorded; while it holds, that height is the section's minimum. The
 * section grows the first time a taller step appears rather than starting at
 * the tallest step, which keeps every question mounted only once.
 */
function useTakeoverSectionMinHeight(
  containerRef: RefObject<HTMLDivElement | null>,
  { isHolding, isMeasuring }: TakeoverSectionMinHeightOptions,
) {
  const tallestHeightRef = useRef(0);
  const measuredWidthRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    const section = containerRef.current?.closest("section");
    if (!isMeasuring || !section) {
      return;
    }

    const recordHeight = () => {
      // A width change reflows every step, so the tallest height recorded at
      // the old width no longer applies: measure again from the section's
      // natural height rather than holding a height that may now be too tall.
      const { width } = section.getBoundingClientRect();
      if (width !== measuredWidthRef.current) {
        measuredWidthRef.current = width;
        tallestHeightRef.current = 0;
        section.style.minHeight = "";
      }

      const { height } = section.getBoundingClientRect();
      if (height <= tallestHeightRef.current) {
        return;
      }

      tallestHeightRef.current = height;
      if (isHolding) {
        section.style.minHeight = `${height}px`;
      }
    };
    recordHeight();
    const observer = new ResizeObserver(recordHeight);
    observer.observe(section);
    return () => observer.disconnect();
  }, [containerRef, isHolding, isMeasuring]);

  useLayoutEffect(() => {
    const section = containerRef.current?.closest("section");
    if (!isHolding || !section || tallestHeightRef.current === 0) {
      return;
    }

    section.style.minHeight = `${tallestHeightRef.current}px`;
    return () => {
      section.style.minHeight = "";
    };
  }, [containerRef, isHolding]);
}

export { useTakeoverSectionMinHeight };
